import React, { useState, useEffect, useContext } from 'react';
import { ServerConfigContext } from '../context/ServerConfigContext';
import { createApiService } from '../utils/apiService';
import { transformApiData, getRateColorClass } from '../utils/formatUtils';
import TradingTable from '../components/TradingTable';
import './TradingPages.css';

// 쿠키 유틸리티 함수
const setCookie = (name, value, days = 30) => {
  try {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    const cookieValue = encodeURIComponent(JSON.stringify(value));
    document.cookie = `${name}=${cookieValue};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
    console.log(`쿠키 저장 성공: ${name}`, value);
  } catch (error) {
    console.error('쿠키 저장 오류:', error);
  }
};

const getCookie = (name) => {
  try {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    console.log('모든 쿠키:', document.cookie);
    for(let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        const cookieValue = c.substring(nameEQ.length, c.length);
        const decodedValue = decodeURIComponent(cookieValue);
        const parsedValue = JSON.parse(decodedValue);
        console.log(`쿠키 불러오기 성공: ${name}`, parsedValue);
        return parsedValue;
      }
    }
    console.log(`쿠키 찾을 수 없음: ${name}`);
    return null;
  } catch (error) {
    console.error('쿠키 불러오기 오류:', error);
    return null;
  }
};

// 기본 시간 설정 함수
const getDefaultTime = (dateString) => {
  const now = new Date();
  // 로컬 시간 기준으로 오늘 날짜 생성 (YYYY-MM-DD)
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const today = `${year}-${month}-${day}`;
  
  console.log(`시간 설정 - 현재 로컬 시간: ${now.toLocaleString()}, 오늘: ${today}, 조회 날짜: ${dateString}`);
  
  if (dateString === today) {
    // 오늘 날짜인 경우
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    console.log(`오늘 날짜 - 현재 시각: ${currentHour}:${currentMinute}`);
    
    if (currentHour < 15 || (currentHour === 15 && currentMinute === 0)) {
      // 15:00 이전인 경우 현재 시각 사용
      const timeString = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
      
      // 거래 시간 범위 (09:00~15:30) 내에서만 설정
      if (currentHour >= 9 && currentHour <= 15) {
        if (currentHour === 15 && currentMinute > 30) {
          console.log(`시간 설정 결과: 15:30 (15:30 이후이므로 조정)`);
          return '15:30';
        }
        console.log(`시간 설정 결과: ${timeString} (현재 시각 사용)`);
        return timeString;
      } else if (currentHour < 9) {
        console.log(`시간 설정 결과: 09:00 (거래시간 전이므로 조정)`);
        return '09:00';
      } else {
        console.log(`시간 설정 결과: 15:30 (거래시간 후이므로 조정)`);
        return '15:30';
      }
    } else {
      // 15:00 이후인 경우 15:00로 설정
      console.log(`시간 설정 결과: 15:00 (15:00 이후이므로 15:00으로 설정)`);
      return '15:00';
    }
  } else {
    // 오늘이 아닌 경우 15:00으로 설정
    console.log(`시간 설정 결과: 15:00 (과거/미래 날짜이므로 15:00으로 설정)`);
    return '15:00';
  }
};

const PressedPage = () => {
  const { getServer, tableSettings, filterStockData, isConfigLoaded } = useContext(ServerConfigContext);
  
  // 로컬 시간 기준으로 오늘 날짜 설정
  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [selectedTime, setSelectedTime] = useState(() => getDefaultTime(getLocalDateString()));
  const [timeUnit, setTimeUnit] = useState('분'); // '분', '시', '일'
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tradingData, setTradingData] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  // 등락률 조건 상태 (쿠키에서 불러오기)
  const [percentageThreshold, setPercentageThreshold] = useState(() => {
    const savedThreshold = getCookie('pressedPagePercentageThreshold');
    return savedThreshold !== null ? savedThreshold : '-1.0';
  });
  const [filterCondition, setFilterCondition] = useState(() => {
    const savedCondition = getCookie('pressedPageFilterCondition');
    return savedCondition !== null ? savedCondition : '이하';
  });
  // 최대 순위 필터 (쿠키에서 불러오기)
  const [maxRank, setMaxRank] = useState(() => {
    const savedMaxRank = getCookie('pressedPageMaxRank');
    return savedMaxRank !== null ? savedMaxRank : '';
  });
  // 시총 필터 (억원 단위, 쿠키에서 불러오기)
  const [marketCapFilter, setMarketCapFilter] = useState(() => {
    const savedMarketCap = getCookie('pressedPageMarketCapFilter');
    return savedMarketCap !== null ? savedMarketCap : '';
  });
  const [marketCapCondition, setMarketCapCondition] = useState(() => {
    const savedCondition = getCookie('pressedPageMarketCapCondition');
    return savedCondition !== null ? savedCondition : '이상';
  });
  const [showSettings, setShowSettings] = useState(false);
  // 기본 상세 정보 설정
  const defaultVisibleDetails = {
    sector: true,
    per: true,
    pbr: true,
    foreignBuy: true,
    institutionBuy: true,
    individualBuy: true,
    high52Week: true,
    low52Week: true,
    volume52Week: true
  };
  
  const [visibleDetails, setVisibleDetails] = useState(() => {
    const savedDetails = getCookie('pressedPageVisibleDetails');
    
    const finalDetails = { ...defaultVisibleDetails };
    if (savedDetails && typeof savedDetails === 'object') {
      Object.keys(defaultVisibleDetails).forEach(key => {
        if (savedDetails[key] !== undefined && savedDetails[key] !== null) {
          finalDetails[key] = savedDetails[key];
        }
      });
    }
    
    return finalDetails;
  });
  
  // 종목정보 전체 표시 여부 상태
  const [showDetailsSection, setShowDetailsSection] = useState(() => {
    const savedShowDetails = getCookie('pressedPageShowDetailsSection');
    return savedShowDetails !== undefined ? savedShowDetails : true;
  });

  // setShowDetailsSection 래퍼 함수
  const handleSetShowDetailsSection = (newValue) => {
    setShowDetailsSection(newValue);
  };

  // 기본 차트 설정
  const defaultChartSettings = {
    upColor: '#d32f2f',
    downColor: '#1976d2', 
    wickColor: '#424242',
    wickThickness: 1,
    bodyThickness: 0.8,
    showMA5: true,
    showMA10: true,
    showMA20: true,
    showMA60: false,
    showMA120: false,
    ma5Color: '#ff9800',
    ma10Color: '#4caf50',
    ma20Color: '#9c27b0',
    ma60Color: '#f44336',
    ma120Color: '#795548',
    showGrid: true,
    gridColor: '#e0e0e0',
    dailyCandleCount: 40,
    minuteCandleCount: 380
  };
  
  const [chartSettings, setChartSettings] = useState(() => {
    const savedSettings = getCookie('pressedPageChartSettings');
    
    // 기본값과 저장된 설정을 병합하되, undefined나 빈 문자열 값은 기본값으로 대체
    const finalSettings = { ...defaultChartSettings };
    if (savedSettings && typeof savedSettings === 'object') {
      Object.keys(defaultChartSettings).forEach(key => {
        if (savedSettings[key] !== undefined && savedSettings[key] !== null && savedSettings[key] !== '') {
          finalSettings[key] = savedSettings[key];
        }
      });
    }
    
    return finalSettings;
  });
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = getCookie('pressedPageActiveTab');
    return (savedTab && typeof savedTab === 'string') ? savedTab : 'chart';
  });

  const fetchAvailableDates = async () => {
    try {
      console.log('📅 [PressedPage] 날짜 목록 조회 시작...');
      setLoading(true);
      const server = getServer('stock-api-server');

      if (!server) {
        console.error('❌ stock-api-server를 찾을 수 없습니다.');
        setIsInitializing(false);
        return;
      }

      if (!server.isActive) {
        console.error('❌ stock-api-server가 비활성 상태입니다.');
        setIsInitializing(false);
        return;
      }

      console.log('🔗 API 서버:', server.baseUrl);
      const apiService = createApiService(server);
      console.log('🚀 /api/v1/ranks API 호출 중...');
      const dateList = await apiService.get('/api/v1/ranks');
      console.log('✅ 날짜 목록 조회 성공:', dateList);

      setAvailableDates(dateList);

      // 현재 선택된 날짜가 조회 가능한 날짜 목록에 없으면 가장 최근 날짜로 설정
      if (dateList.length > 0) {
        const currentSelectedDateFormatted = selectedDate.replace(/-/g, '');
        if (!dateList.includes(currentSelectedDateFormatted)) {
          const latestDate = dateList.sort().reverse()[0]; // 가장 최근 날짜
          const formattedDate = `${latestDate.substring(0, 4)}-${latestDate.substring(4, 6)}-${latestDate.substring(6, 8)}`;
          console.log(`📅 [PressedPage] 가장 최근 날짜로 자동 설정: ${formattedDate}`);
          setSelectedDate(formattedDate);
        }
      }
    } catch (error) {
      console.error('❌ 조회 가능한 날짜를 가져오는 중 오류 발생:', error);
    } finally {
      setLoading(false);
      setIsInitializing(false);
    }
  };

  const isDateAvailable = (date) => {
    const formattedDate = date.replace(/-/g, '');
    return availableDates.includes(formattedDate);
  };

  const handleDateChange = (newDate) => {
    if (isDateAvailable(newDate)) {
      setSelectedDate(newDate);
    }
  };

  const isTimeInValidRange = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const timeInMinutes = hours * 60 + minutes;
    const startTime = 9 * 60;
    const endTime = 15 * 60 + 30;
    
    return timeInMinutes >= startTime && timeInMinutes <= endTime;
  };

  const isSelectedDateToday = (dateString) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const today = `${year}-${month}-${day}`;
    
    return dateString === today;
  };

  const isTimeAfterNow = (timeString) => {
    const now = new Date();
    const [hours, minutes] = timeString.split(':').map(Number);
    const selectedTime = new Date();
    selectedTime.setHours(hours, minutes, 0, 0);
    
    return selectedTime > now;
  };

  const isTimeValid = (dateString, timeString) => {
    if (!isTimeInValidRange(timeString)) {
      return false;
    }
    
    if (isSelectedDateToday(dateString)) {
      return !isTimeAfterNow(timeString);
    }
    
    return true;
  };

  // 시간 변경 핸들러 - 유효성 검사 없이 바로 상태 반영
  const handleTimeChange = (newTime) => {
    setSelectedTime(newTime);
  };


  const filterDataByPercentage = (data) => {
    if (!data || data.length === 0) {
      return data;
    }

    return data.filter(item => {
      // 1. 등락률 필터
      const threshold = parseFloat(percentageThreshold);
      if (!isNaN(threshold)) {
        const rateValue = item.rateValue || 0;
        if (filterCondition === '이상') {
          if (rateValue < threshold) return false;
        } else {
          if (rateValue > threshold) return false;
        }
      }

      // 2. 최대 순위 필터
      const maxRankValue = parseInt(maxRank);
      if (!isNaN(maxRankValue) && maxRankValue > 0) {
        const rankValue = parseInt(item.rank);
        if (!isNaN(rankValue) && rankValue > maxRankValue) {
          return false;
        }
      }

      // 3. 시총 필터 (억원 단위)
      const marketCapValue = parseFloat(marketCapFilter);
      if (!isNaN(marketCapValue) && marketCapValue > 0) {
        // item.marketCap은 "2,000억" 또는 "1.5조" 형식의 문자열
        // 숫자만 추출하여 억원 단위로 변환
        const marketCapText = item.marketCap || '';
        let itemMarketCapInBillion = 0;

        if (marketCapText.includes('조')) {
          // "1.5조" → 15000억
          const trillion = parseFloat(marketCapText.replace(/[^0-9.]/g, ''));
          itemMarketCapInBillion = trillion * 10000;
        } else if (marketCapText.includes('억')) {
          // "2,000억" → 2000억
          itemMarketCapInBillion = parseFloat(marketCapText.replace(/[^0-9.]/g, ''));
        }

        if (marketCapCondition === '이상') {
          if (itemMarketCapInBillion < marketCapValue) return false;
        } else {
          if (itemMarketCapInBillion > marketCapValue) return false;
        }
      }

      return true;
    });
  };

  const fetchTradingData = async (date, time) => {
    try {
      setIsSearching(true);
      setIsInitializing(false);
      // 이전 데이터 유지 - 깜빡임 방지

      const server = getServer('stock-api-server');

      if (!server) {
        console.error('stock-api-server를 찾을 수 없습니다.');
        setTradingData([]); // 서버 없으면 표 비우기
        return;
      }

      if (!server.isActive) {
        console.error('stock-api-server가 비활성 상태입니다.');
        setTradingData([]); // 서버 비활성이면 표 비우기
        return;
      }

      const apiService = createApiService(server);

      const formattedDate = date.replace(/-/g, '');
      const formattedTime = time.replace(':', '');

      const response = await apiService.get(`/api/v1/ranks/${formattedDate}`, {
        time: formattedTime,
        offset: 0,
        limit: 100
      });

      console.log('거래대금 데이터 조회 성공:', response);
      const rawData = response.data || response;

      // 데이터가 없거나 빈 배열이면 표 비우기
      if (!rawData || (Array.isArray(rawData) && rawData.length === 0)) {
        console.log('조회 결과 데이터 없음 - 표 비우기');
        setTradingData([]);
        return;
      }

      const transformedData = Array.isArray(rawData) ? rawData.map(transformApiData) : [];
      const stockNameFilteredData = filterStockData(transformedData); // 종목명 필터 적용
      const percentageFilteredData = filterDataByPercentage(stockNameFilteredData); // 등락률 필터 적용

      // rank 기준 오름차순 정렬 (원본 배열을 변경하지 않도록 복사 후 정렬)
      const sortedData = [...percentageFilteredData].sort((a, b) => {
        const rankA = parseInt(a.rank) || 0;
        const rankB = parseInt(b.rank) || 0;
        return rankA - rankB;
      });

      // 필터 적용 후 데이터가 없으면 표 비우기
      if (sortedData.length === 0) {
        console.log('필터 적용 후 데이터 없음 - 표 비우기');
      }

      setTradingData(sortedData);
    } catch (error) {
      console.error('거래대금 데이터 조회 실패:', error);
      // 에러 시 표 비우기
      setTradingData([]);
      alert(`데이터 조회에 실패했습니다: ${error.message || '네트워크 오류'}`);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePrevious = () => {
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const currentDate = new Date(selectedDate);
    let shouldFetch = false;
    let newDate = selectedDate;
    let newTime = selectedTime;

    switch(timeUnit) {
      case '분':
        // 분만 변경, 날짜와 시간은 고정
        let newMinutes = minutes - 1;
        let newHours = hours;

        if (newMinutes < 0) {
          newMinutes = 59;
          newHours = hours - 1;
        }

        const newTimeString = `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;

        // 시간 제한 확인
        if (isTimeValid(selectedDate, newTimeString)) {
          setSelectedTime(newTimeString);
          newTime = newTimeString;
          shouldFetch = true;
        }
        break;
      case '시':
        // 시간만 변경, 날짜는 고정
        const newHours_h = hours - 1;
        const newTimeString_h = `${newHours_h.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

        // 시간 제한 확인
        if (newHours_h >= 0 && isTimeValid(selectedDate, newTimeString_h)) {
          setSelectedTime(newTimeString_h);
          newTime = newTimeString_h;
          shouldFetch = true;
        }
        break;
      case '일':
        // 날짜만 변경, 시간은 그대로
        const prevDate = new Date(currentDate);
        prevDate.setDate(prevDate.getDate() - 1);
        const prevDateString = prevDate.toISOString().split('T')[0];
        setSelectedDate(prevDateString);
        newDate = prevDateString;
        shouldFetch = true;
        break;
    }

    // 값이 변경되었으면 자동으로 조회
    if (shouldFetch) {
      // 약간의 지연을 두어 상태 업데이트가 완료된 후 조회
      setTimeout(() => {
        fetchTradingData(newDate, newTime);
      }, 50);
    }
  };

  const handleNext = () => {
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const currentDate = new Date(selectedDate);
    let shouldFetch = false;
    let newDate = selectedDate;
    let newTime = selectedTime;

    switch(timeUnit) {
      case '분':
        // 분만 변경, 날짜와 시간은 고정
        let newMinutes_n = minutes + 1;
        let newHours_n = hours;

        if (newMinutes_n >= 60) {
          newMinutes_n = 0;
          newHours_n = hours + 1;
        }

        const newTimeString_n = `${newHours_n.toString().padStart(2, '0')}:${newMinutes_n.toString().padStart(2, '0')}`;

        // 시간 제한 확인
        if (newHours_n < 24 && isTimeValid(selectedDate, newTimeString_n)) {
          setSelectedTime(newTimeString_n);
          newTime = newTimeString_n;
          shouldFetch = true;
        }
        break;
      case '시':
        // 시간만 변경, 날짜는 고정
        const newHours_nh = hours + 1;
        const newTimeString_nh = `${newHours_nh.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

        // 시간 제한 확인
        if (newHours_nh < 24 && isTimeValid(selectedDate, newTimeString_nh)) {
          setSelectedTime(newTimeString_nh);
          newTime = newTimeString_nh;
          shouldFetch = true;
        }
        break;
      case '일':
        // 날짜만 변경, 시간은 그대로
        const nextDate = new Date(currentDate);
        nextDate.setDate(nextDate.getDate() + 1);
        const nextDateString = nextDate.toISOString().split('T')[0];
        setSelectedDate(nextDateString);
        newDate = nextDateString;
        shouldFetch = true;
        break;
    }

    // 값이 변경되었으면 자동으로 조회
    if (shouldFetch) {
      // 약간의 지연을 두어 상태 업데이트가 완료된 후 조회
      setTimeout(() => {
        fetchTradingData(newDate, newTime);
      }, 50);
    }
  };

  const handleSearch = () => {
    // 유효성 검사 - 조회 버튼 클릭 시에만 실행
    if (!isTimeInValidRange(selectedTime)) {
      alert('거래 시간(09:00 ~ 15:30) 범위 내의 시간만 선택할 수 있습니다.');
      return;
    }

    if (isSelectedDateToday(selectedDate) && isTimeAfterNow(selectedTime)) {
      const now = new Date();
      const currentHour = now.getHours().toString().padStart(2, '0');
      const currentMinute = now.getMinutes().toString().padStart(2, '0');
      alert(`현재 시간(${currentHour}:${currentMinute})까지만 선택할 수 있습니다.\n미래 시간은 선택할 수 없습니다.`);
      return;
    }

    if (availableDates.length > 0 && isDateAvailable(selectedDate) && isTimeValid(selectedDate, selectedTime)) {
      console.log('조회 요청:', { selectedDate, selectedTime, timeUnit });
      fetchTradingData(selectedDate, selectedTime);
    } else if (availableDates.length > 0) {
      console.warn('유효하지 않은 날짜 또는 시간입니다.');
    }
  };

  // 서버 설정이 로드된 후에 데이터 초기화
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 5;
    const retryInterval = 300;

    const initializeData = async () => {
      try {
        console.log('🚀 PressedPage 초기화 시작...', { isConfigLoaded, retryCount });
        
        // 서버 설정이 실제로 사용 가능한지 확인
        const server = getServer('stock-api-server');
        if (server && server.isActive) {
          console.log('✅ PressedPage - 서버 설정 확인됨, 데이터 초기화 시작');
          await fetchAvailableDates();
          return;
        }
        
        // 서버 설정이 없거나 비활성 상태일 때 재시도
        if (retryCount < maxRetries) {
          retryCount++;
          console.warn(`⚠️ PressedPage - 서버 설정 대기 중 (${retryCount}/${maxRetries})...`);
          setTimeout(initializeData, retryInterval);
        } else {
          console.error('❌ PressedPage - 서버 설정 초기화 최대 재시도 실패');
        }
      } catch (error) {
        console.error('❌ PressedPage - 초기화 중 오류:', error);
      }
    };

    // isConfigLoaded가 true가 되거나 일정 시간 후 시작
    if (isConfigLoaded) {
      initializeData();
    } else {
      // 설정 로드를 기다리지만 최대 2초 후에는 강제 시작
      const forceInitTimeout = setTimeout(() => {
        console.warn('⚠️ PressedPage - 설정 로드 대기 시간 초과, 강제 초기화 시작');
        initializeData();
      }, 2000);

      // isConfigLoaded가 true가 되면 타이머 정리
      if (isConfigLoaded) {
        clearTimeout(forceInitTimeout);
      }

      return () => clearTimeout(forceInitTimeout);
    }
  }, [isConfigLoaded, getServer]);

  // 날짜 변경 시 적절한 기본 시간으로 자동 조정
  useEffect(() => {
    const defaultTime = getDefaultTime(selectedDate);
    if (selectedTime !== defaultTime) {
      console.log(`날짜 변경에 따른 시간 자동 조정: ${selectedTime} → ${defaultTime}`);
      setSelectedTime(defaultTime);
    }
  }, [selectedDate]);

  // availableDates가 업데이트되면 초기 데이터 로드
  useEffect(() => {
    if (availableDates.length > 0) {
      // 가장 최근 날짜로 설정하고 적절한 시간으로 조정
      const latestDate = availableDates.sort().reverse()[0]; // 가장 최근 날짜
      const formattedDate = `${latestDate.substring(0, 4)}-${latestDate.substring(4, 6)}-${latestDate.substring(6, 8)}`;
      const appropriateTime = getDefaultTime(formattedDate);
      
      console.log(`초기 데이터 로드 - 최신 날짜: ${formattedDate}, 적절한 시간: ${appropriateTime}`);
      
      // 날짜와 시간을 동시에 업데이트
      if (selectedDate !== formattedDate || selectedTime !== appropriateTime) {
        setSelectedDate(formattedDate);
        setSelectedTime(appropriateTime);
        
        // 바로 데이터 조회 (상태 업데이트 후)
        setTimeout(() => {
          if (isDateAvailable(formattedDate) && isTimeValid(formattedDate, appropriateTime)) {
            fetchTradingData(formattedDate, appropriateTime).finally(() => {
              setIsInitializing(false);
            });
          } else {
            setIsInitializing(false);
          }
        }, 50);
      } else if (isDateAvailable(selectedDate) && isTimeValid(selectedDate, selectedTime)) {
        // 이미 올바른 날짜와 시간이 설정되어 있으면 바로 조회
        fetchTradingData(selectedDate, selectedTime).finally(() => {
          setIsInitializing(false);
        });
      } else {
        setIsInitializing(false);
      }
    }
  }, [availableDates]);

  // 초기 시간 설정 제거 - getDefaultTime()에서 이미 적절한 시간 설정함

  // 날짜가 변경될 때 오늘 날짜인 경우 시간 조정
  useEffect(() => {
    if (isSelectedDateToday(selectedDate) && isTimeAfterNow(selectedTime)) {
      const now = new Date();
      const currentHour = now.getHours().toString().padStart(2, '0');
      const currentMinute = now.getMinutes().toString().padStart(2, '0');
      const currentTimeString = `${currentHour}:${currentMinute}`;
      
      if (isTimeInValidRange(currentTimeString)) {
        setSelectedTime(currentTimeString);
      } else if (now.getHours() < 9) {
        setSelectedTime('09:00');
      } else {
        setSelectedTime('15:30');
      }
    }
  }, [selectedDate]);

  // chartSettings 변경 시 쿠키에 저장 (초기 로드 제외)
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }
    console.log('차트 설정 저장 - MA10:', chartSettings.showMA10, 'MA10Color:', chartSettings.ma10Color);
    setCookie('pressedPageChartSettings', chartSettings);
  }, [chartSettings, isInitialLoad]);

  // visibleDetails 변경 시 쿠키에 저장
  useEffect(() => {
    setCookie('pressedPageVisibleDetails', visibleDetails);
  }, [visibleDetails]);

  // activeTab 변경 시 쿠키에 저장
  useEffect(() => {
    setCookie('pressedPageActiveTab', activeTab);
  }, [activeTab]);

  // showDetailsSection 변경 시 쿠키에 저장
  useEffect(() => {
    setCookie('pressedPageShowDetailsSection', showDetailsSection);
  }, [showDetailsSection]);

  // percentageThreshold 변경 시 쿠키에 저장
  useEffect(() => {
    setCookie('pressedPagePercentageThreshold', percentageThreshold);
  }, [percentageThreshold]);

  // filterCondition 변경 시 쿠키에 저장
  useEffect(() => {
    setCookie('pressedPageFilterCondition', filterCondition);
  }, [filterCondition]);

  // maxRank 변경 시 쿠키에 저장
  useEffect(() => {
    setCookie('pressedPageMaxRank', maxRank);
  }, [maxRank]);

  // marketCapFilter 변경 시 쿠키에 저장
  useEffect(() => {
    setCookie('pressedPageMarketCapFilter', marketCapFilter);
  }, [marketCapFilter]);

  // marketCapCondition 변경 시 쿠키에 저장
  useEffect(() => {
    setCookie('pressedPageMarketCapCondition', marketCapCondition);
  }, [marketCapCondition]);

  return (
    <div className="page-container">
      {/* 날짜/시간 선택 및 제어 패널 */}
      <div className="pressed-datetime-control-panel">
        <div className="pressed-datetime-inputs">
          <div className="pressed-input-group">
            <label>날짜</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className={`pressed-date-input ${!isDateAvailable(selectedDate) ? 'pressed-invalid-date' : ''}`}
              title={!isDateAvailable(selectedDate) ? '조회할 수 없는 날짜입니다' : ''}
            />
          </div>

          <div className="pressed-input-group">
            <label>시간</label>
            <input
              type="time"
              value={selectedTime}
              onChange={(e) => handleTimeChange(e.target.value)}
              min="09:00"
              max={isSelectedDateToday(selectedDate) ?
                (() => {
                  const now = new Date();
                  const currentHour = now.getHours().toString().padStart(2, '0');
                  const currentMinute = now.getMinutes().toString().padStart(2, '0');
                  return `${currentHour}:${currentMinute}`;
                })() : '15:30'
              }
              className={`pressed-time-input ${!isTimeValid(selectedDate, selectedTime) ? 'pressed-invalid-time' : ''}`}
              title={
                !isTimeValid(selectedDate, selectedTime)
                  ? (isSelectedDateToday(selectedDate) && isTimeAfterNow(selectedTime)
                    ? '현재 시간까지만 선택 가능합니다'
                    : '09:00 ~ 15:30 시간대만 선택 가능합니다')
                  : ''
              }
            />
          </div>
        </div>

        <div className="pressed-control-buttons">
          <div className="pressed-percentage-filter-group">
            <div className="pressed-input-group">
              <label>등락률</label>
              <input
                type="number"
                value={percentageThreshold}
                onChange={(e) => setPercentageThreshold(e.target.value)}
                placeholder="예: -1.0"
                step="0.1"
                className="pressed-percentage-input"
              />
            </div>
            <div className="pressed-input-group">
              <label>조건</label>
              <select
                value={filterCondition}
                onChange={(e) => setFilterCondition(e.target.value)}
                className="pressed-condition-select"
              >
                <option value="이상">이상</option>
                <option value="이하">이하</option>
              </select>
            </div>
            <div className="pressed-input-group">
              <label>최대 순위</label>
              <input
                type="text"
                value={maxRank}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    setMaxRank(value);
                  }
                }}
                placeholder="예: 35"
                className="pressed-rank-input"
              />
            </div>
            <div className="pressed-input-group">
              <label>시총(억원)</label>
              <input
                type="text"
                value={marketCapFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d+(\.\d*)?$/.test(value)) {
                    setMarketCapFilter(value);
                  }
                }}
                placeholder="예: 1000"
                className="pressed-marketcap-input"
              />
            </div>
            <div className="pressed-input-group">
              <label>조건</label>
              <select
                value={marketCapCondition}
                onChange={(e) => setMarketCapCondition(e.target.value)}
                className="pressed-condition-select"
              >
                <option value="이상">이상</option>
                <option value="이하">이하</option>
              </select>
            </div>
          </div>
          <button
            onClick={handleSearch}
            className="pressed-search-btn"
            disabled={loading || isSearching || !isDateAvailable(selectedDate) || !isTimeValid(selectedDate, selectedTime)}
          >
            {isSearching ? '조회 중...' : '조회'}
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="pressed-settings-btn"
            title="설정"
          >
            ⚙️
          </button>
        </div>
      </div>

      <TradingTable 
        key="pressed-page-trading-table"
        pageId="PressedPage"
        data={tradingData}
        isLoading={isInitializing}
        isSearching={isSearching}
        loadingMessage="데이터를 조회하고 있습니다..."
        initialLoadingMessage="초기 데이터를 로딩하고 있습니다..."
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        visibleDetails={visibleDetails}
        setVisibleDetails={setVisibleDetails}
        chartSettings={chartSettings}
        setChartSettings={setChartSettings}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        selectedDate={selectedDate}
        showDetailsSection={showDetailsSection}
        setShowDetailsSection={handleSetShowDetailsSection}
      />

      <div className="last-updated">
        <span>마지막 업데이트: {new Date().toLocaleString()}</span>
      </div>
    </div>
  );
};

export default PressedPage;